import { useQuery } from '@tanstack/react-query';
import { compact } from 'lodash';
import { GroupBase } from 'react-select';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { UserId } from '@/portainer/users/types';
import { withGlobalError } from '@/react-tools/react-query';
import { useCurrentUser } from '@/react/hooks/useUser';
import { useEnvironmentRegistries } from '@/react/portainer/environments/queries/useEnvironmentRegistries';
import { Option } from '@/react/components/form-components/PortainerSelect';
import { RegistryTypes } from '@/portainer/models/registryTypes';

import { HelmRegistriesResponse } from '../types';
import { RegistryValue } from '../components/HelmRegistrySelect';

import { queryKeys } from './query-keys';

export const OCI_REGISTRY_OPTION_LABEL = 'OCI Registries';
export const HELM_REPO_OPTION_LABEL = 'Helm Repositories';

/**
 * Hook to fetch all Helm registries for the current user
 */
export function useUserHelmRepositories<T = HelmRegistriesResponse>({
  select,
}: {
  select?: (registries: HelmRegistriesResponse) => T;
} = {}) {
  const { user } = useCurrentUser();
  return useQuery(
    queryKeys.registries(user.Id),
    async () => getUserHelmRepositories(user.Id),
    {
      enabled: !!user.Id,
      select,
      ...withGlobalError('Unable to retrieve helm registries'),
    }
  );
}

/**
 * Get Helm repositories for user
 */
async function getUserHelmRepositories(userId: UserId) {
  try {
    const { data } = await axios.get<HelmRegistriesResponse>(
      `users/${userId}/helm/repositories`
    );
    return data;
  } catch (err) {
    throw parseAxiosError(err, 'Unable to retrieve helm repositories for user');
  }
}

export function useHelmRegistryRepoOptions(
  environmentId: number,
  namespace?: string
) {
  const helmUserReposQuery = useUserHelmRepositories({
    select: (registries) => {
      const registryArray = flattenHelmRegistries(registries);
      return registryArray
        .map<Option<RegistryValue>>((registry) => ({
          label: registry,
          value: {
            registryUrl: registry,
            isOCI: false,
            name: registry,
          },
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  });

  const portainerRegistryOptionsQuery = useEnvironmentRegistries(
    environmentId,
    {
      namespace,
      hideDefault: true,
      enabled: !!environmentId && !!namespace,
      select: (registries) =>
        registries
          // filter out dockerhub and quay registries until we support registries that don't list repos https://forums.docker.com/t/registry-v2-catalog/45368
          // TODO add this option again as part of https://linear.app/portainer/issue/R8S-369/support-oci-registries-that-dont-allow-listing-repositories
          .filter(
            (registry) =>
              registry.Type !== RegistryTypes.DOCKERHUB &&
              registry.Type !== RegistryTypes.QUAY
          )
          .map<Option<RegistryValue>>((registry) => ({
            label: registry.Name,
            value: {
              registryId: registry.Id,
              registryUrl: registry.URL,
              isOCI: true,
              name: registry.Name,
              type: registry.Type,
              projectPath: registry.Gitlab?.ProjectPath,
            },
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
    }
  );

  const registryOptions: GroupBase<Option<RegistryValue>>[] = [
    {
      label: OCI_REGISTRY_OPTION_LABEL,
      options: portainerRegistryOptionsQuery.data ?? [],
    },
    {
      label: HELM_REPO_OPTION_LABEL,
      options: helmUserReposQuery.data ?? [],
    },
  ];

  return {
    registryOptions,
    isLoading:
      helmUserReposQuery.isInitialLoading ||
      portainerRegistryOptionsQuery.isInitialLoading,
    isError:
      helmUserReposQuery.isError || portainerRegistryOptionsQuery.isError,
  };
}

/** get the unique global and user registries in one array */
export function flattenHelmRegistries(registries: HelmRegistriesResponse) {
  // compact will remove the global repository if it's empty
  const repos = compact([
    registries.GlobalRepository.toLowerCase(),
    ...registries.UserRepositories.map((repo) => repo.URL.toLowerCase()),
  ]);
  return [...new Set(repos)];
}
